import {
  Button,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useCreateClientPolicyMutation,
  useUpdateClientPolicyMutation,
} from '@omnipresentgroup/domains';
import { Form, FormProvider } from '@omnipresentgroup/flexible-forms';
import {
  type ClientPolicy,
  ClientPolicyCategory,
  type ClientPolicyPeriod,
  PayTypeEnum,
} from '@omnipresentgroup/types';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useClientLeaveSettingFormSchema } from '../hooks';
import { LeaveSettingsCutOffDayNotification } from './LeaveSettingsCutOffDayNotification';

const ACTIVATE_TEXT = 'Activate';
const SAVE_TEXT = 'Save';
const EDIT_TEXT = 'Edit';
const CANCEL_TEXT = 'Cancel';
const MODAL_TITLE = (category: ClientPolicyCategory) =>
  `${
    category === ClientPolicyCategory.STATUTORY ? ACTIVATE_TEXT : EDIT_TEXT
  } custom leave policy`;
const NOTIFICATION_TITLE = (countryName: string, description: string) =>
  `In ${countryName}, the statutory leave policy is ${description}.`;

const MESSAGES = {
  activate: 'Custom policy has been activated successfully',
  edit: 'Custom policy has been updated successfully',
} as const;

type LeaveSettingUpdateModalProps = {
  companyId: string;
  policy: ClientPolicy;
  statutoryDescription: string;
  action: 'activate' | 'edit';
  trigger: ModalProps['trigger'];
};

export const LeaveSettingUpdateModal = ({
  companyId,
  policy,
  statutoryDescription,
  action,
  trigger,
}: LeaveSettingUpdateModalProps) => {
  const { modalProps } = useModal({ trigger });
  const [modalOpen, setModalOpen] = useState(false);

  const [formValues, setFormValues] = useState<ClientPolicyPeriod>();

  const formId = useMemo(
    () => `leave-setting-form-${policy?.country.code}`,
    [policy],
  );

  const currentPolicyPeriod = useMemo(
    () => (policy?.periods.length === 1 ? policy?.periods[0] : undefined),
    [policy],
  );

  const initialFormValues = useMemo(
    () =>
      policy.category === ClientPolicyCategory.CUSTOM
        ? currentPolicyPeriod || policy?.customMinimum
        : policy?.customMinimum,
    [currentPolicyPeriod, policy.category, policy?.customMinimum],
  );

  const formValuesToPeriod = useCallback(
    (values: ClientPolicyPeriod) => ({
      ...values,
      ...(values.payDetails.payType === PayTypeEnum.PERCENTAGE && {
        payDetails: {
          ...values.payDetails,
          percentageValue: Number.parseInt(
            String(values.payDetails.percentageValue).replace('%', ''),
          ),
        },
      }),
    }),
    [],
  );

  const isActionDisabled = useMemo(
    () =>
      formValues &&
      isEqual(currentPolicyPeriod, formValuesToPeriod(formValues)),
    [currentPolicyPeriod, formValues, formValuesToPeriod],
  );

  const { schema: flexibleSchema, uiSchema: flexibleUiSchema } =
    useClientLeaveSettingFormSchema({
      initialFormValues: initialFormValues,
      customMinimumConfig: policy?.customMinimum,
    });

  const {
    mutate: createCustomPolicy,
    isSuccess: isCreationSuccess,
    isPending: isCreating,
    reset: resetCreation,
  } = useCreateClientPolicyMutation();

  const {
    mutate: updateCustomPolicy,
    isSuccess: isUpdateSuccess,
    isPending: isUpdating,
    reset: resetUpdate,
  } = useUpdateClientPolicyMutation();

  const actionFunction = useMemo(
    () => (action === 'activate' ? createCustomPolicy : updateCustomPolicy),
    [action, createCustomPolicy, updateCustomPolicy],
  );

  const handleSubmit = useCallback(() => {
    if (!formValues || !policy) {
      return;
    }

    actionFunction({
      policy: {
        ...policy,
        category: ClientPolicyCategory.CUSTOM,
        periods: [formValuesToPeriod(formValues)],
      },
      clientId: companyId,
    });
  }, [actionFunction, companyId, formValues, formValuesToPeriod, policy]);

  useEffect(() => {
    if (isCreationSuccess || isUpdateSuccess) {
      notifySuccess({ title: MESSAGES[action] });

      resetCreation();
      resetUpdate();

      setModalOpen(false);
    }
  }, [
    action,
    isCreationSuccess,
    isUpdateSuccess,
    modalOpen,
    resetCreation,
    resetUpdate,
    setModalOpen,
  ]);

  return (
    <Modal
      {...modalProps}
      span={4}
      open={modalOpen}
      onOpenChange={setModalOpen}
    >
      <Modal.Header title={MODAL_TITLE(policy.category)} />
      <Modal.Body>
        <Stack overflow={'hidden'} gap="24">
          <Notification
            id="statutory-policy-notification"
            intent="info"
            title={NOTIFICATION_TITLE(
              policy.country.name,
              statutoryDescription,
            )}
          />
          <LeaveSettingsCutOffDayNotification />
          <FormProvider>
            <Form<ClientPolicyPeriod>
              id={formId}
              schema={flexibleSchema}
              uiSchema={flexibleUiSchema}
              formData={formValues}
              onChange={({ formData }) => {
                formData && setFormValues(formData);
              }}
              onSubmit={handleSubmit}
              liveOmit
              omitExtraData
            />
          </FormProvider>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={() => setModalOpen(false)}
            disabled={isCreating || isUpdating}
          >
            {CANCEL_TEXT}
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={isCreating || isUpdating}
            disabled={isActionDisabled}
          >
            {action === 'activate' ? ACTIVATE_TEXT : SAVE_TEXT}
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
